// The bridge: the only file in src/app/ that imports the Legacy (ADR 0006).
// Its Legacy imports are exactly src/config/app, src/contexts/AuthContext,
// src/lib/data and react-query v3. It goes when the Shell moves.
import { useContext } from "react";
import type { Context } from "react";
import { useQuery } from "react-query";
import AppConfig from "src/config/app";
import { AuthContext } from "src/contexts/AuthContext";
import { roles } from "src/lib/data";
import type { Session } from "./client";

type LegacyAuth = {
  authToken: string;
  userData: { RoleId?: number } | null;
};

const useLegacyAuth = (): LegacyAuth => useContext(AuthContext as unknown as Context<LegacyAuth>);

export const legacyBaseUrl: string = AppConfig.API_URL ?? "";

// Read at request time, as AuthContext stores it. A logged-out user has ''.
export const legacySession: Session = {
  token: () => localStorage.getItem("authToken") || null,
};

// Changes on login, logout and a user switch in the same tab, where the page
// does not reload. AppProviders clears the TanStack cache on it.
export const useLegacySessionKey = (): string => useLegacyAuth().authToken;

// Module requests go through the page's fetch, so Sentry and PostHog see them.
// The Legacy 5xx toast wrapper in src/App.js skips a marked request: a Module
// renders its own errors. Nothing here touches fetch at load (Jest 27 has none).
const moduleRequests = new WeakSet<Request>();

export const markModuleRequest = (request: Request): Request => {
  moduleRequests.add(request);
  return request;
};

export const isModuleRequest = (input: unknown): boolean =>
  typeof input === "object" && input !== null && moduleRequests.has(input as Request);

export type LegacyAction = "read" | "create" | "edit" | "delete";

// The Legacy permission groups a Page or Moved Part asks about, as
// src/lib/permission.js spells them; one literal per group, each pinned in
// legacySession.test.tsx. A typo would hide every edit control from everyone
// but a Super Admin, who bypasses the check.
export type LegacyGroup = "Settings / Integrations";

type RolePermission = { permissionKey?: string } & Partial<Record<LegacyAction, boolean>>;

// The rule of src/permissions/RoutePermission.js, for a Page or Moved Part to
// turn into plain booleans (`canEdit`). The server folds the org's modules into
// the role permissions, so the one role query DefaultLayout loads before any
// route renders is enough. It never fetches: `enabled: false` only subscribes.
// The key literal and the rule are pinned in legacySession.test.tsx.
export function useLegacyCan(group: LegacyGroup, action: LegacyAction): boolean {
  const { authToken, userData } = useLegacyAuth();
  const role = useQuery<RolePermission[]>(["get-user-role-permissions", { authToken }], {
    enabled: false,
  });

  if (userData?.RoleId === roles.SUPER_ADMIN) return true;
  const key = group.toLowerCase();
  return (role.data || []).some(
    (p) => p.permissionKey?.toLowerCase() === key && Boolean(p[action]),
  );
}
